import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { StripeProvider, Elements } from 'react-stripe-elements';

import InjectedCheckoutForm from '../CheckoutForm/InjectedForm';
import PlanPricing from './PlanPricing';
import LineItem from './LineItem';
import { gtag } from '../Analytics/gtag';
import { submitMemberPowerCheckout } from '../../actions/checkout-subscription-actions';
import config from '../../../config';

import { intervalToBillingCycle } from '../Analytics/constants';
import { usePowerFunnelTracking } from '../hooks/usePowerFunnelTracking';

const PowerCheckoutForm = ({
    interval,
    planData,
    onComplete,
    onPaymentAttempted,
    onError,
    hideHeader,
    themeV3,
    hideCardError,
    couponCode,
    couponError,
    applyCoupon,
    invoiceItems,
    onBinCountryMismatch,
    isLoadingPricing,
    renderBeforeLineItem,
    isTennisAustraliaMember,
    savingsPercentage,
    submitButtonText,
    referringSurface,
    gatedFeature,
    disabled,
    onSubmitStart,
    onSubmitAborted,
    onCardError,
    renderCardBrands,
    prismicTitle,
    sourceEventId,
}) => {
    const [stripe, setStripe] = useState(null);
    const dispatch = useDispatch();
    const subscribe = useSelector(state => state.subscribe);
    const { trackPowerCheckoutStarted } = usePowerFunnelTracking({ referringSurface, gatedFeature, prismicTitle, sourceEventId });

    useEffect(() => {
        if (window.Stripe) {
            setStripe(window.Stripe(config.stripeKey));
        } else {
            document.querySelector('#stripe-js').addEventListener('load', () => {
                setStripe(window.Stripe(config.stripeKey));
            });
        }
    }, []);

    const handleComplete = async (paymentMethodId) => {
        if (onPaymentAttempted) {
            onPaymentAttempted();
        }

        const data = { subscriptionId: null };
        data.paymentMethodId = paymentMethodId;

        // Use the actual plan ID from the passed planData
        data.plan = planData?.planId;

        // Add coupon code if available
        if (couponCode) {
            data.couponCode = couponCode;
        }

        gtag('purchase', {
            value: planData?.amountPayPerRecurringInterval,
            transaction_id: paymentMethodId,
            category: 'Subscription Upgrade',
            context: 'power upgrade',
        });

        if (window?.pixie) {
            window.pixie('event', 'Powerplayers');
        }

        // Use Redux action for all checkout logic
        try {
            const powerCheckoutData = { values: data, onBinCountryMismatch, stripe };
            const result = await dispatch(submitMemberPowerCheckout(powerCheckoutData)); // NOSONAR — Redux thunk returns a promise

            // Check if this was a BIN country mismatch (not a successful subscription)
            if (result.isSubscribed === false && result.availablePowerPlans) {
                // BIN country mismatch handled by Redux action callback
                return;
            }

            // Success case
            onComplete(paymentMethodId);
        } catch (error) {
            // Redux still handles error display; onError is an optional telemetry hook.
            if (onError) {
                onError(error?.message || error);
            }
        }
    };

    if (!stripe) {
        return <div data-testid="power-checkout-form.loading">Loading...</div>;
    }

    const renderPricing = () => {
        // Don't show anything while pricing is loading to prevent flicker
        if (isLoadingPricing) {
            return null;
        }

        // two different data structures are used for line items
        // 1. invoiceItems is used for v2022 plans which requires an API call
        // 2. planData is used for non-v2022 plans which is already available in the component
        if (invoiceItems) {
            return <LineItem invoiceItems={invoiceItems} planData={planData} />;
        }

        if (planData) {
            return <PlanPricing planData={planData} interval={interval} />;
        }

        return null;
    };

    const handleOnFormSubmit = () => {
        const billingCycle = intervalToBillingCycle[interval] || null;

        trackPowerCheckoutStarted({
            billingCycle,
            priceLocal: planData?.amountPayPerRecurringInterval,
            currency: planData?.currency || 'USD',
        });
    };

    return (
        <div>
            <StripeProvider stripe={stripe}>
                <Elements>
                    <InjectedCheckoutForm
                        formType="powered"
                        interval={interval}
                        onSuccess={handleComplete}
                        hideHeader={hideHeader}
                        themeV3={themeV3}
                        hideCardError={hideCardError}
                        planId={planData?.planId}
                        buttonText={submitButtonText || 'Upgrade to Power'}
                        cardError={subscribe.cardError}
                        submitting={subscribe.loading}
                        couponCode={couponCode}
                        couponError={couponError}
                        applyCoupon={applyCoupon}
                        renderv3powerpricing={renderPricing}
                        renderBeforeLineItem={renderBeforeLineItem}
                        isTennisAustraliaMember={isTennisAustraliaMember}
                        savingsPercentage={savingsPercentage}
                        isLoadingPricing={isLoadingPricing}
                        disabled={disabled}
                        onSubmitStart={onSubmitStart}
                        onSubmitAborted={onSubmitAborted}
                        onCardError={onCardError}
                        onFormSubmit={handleOnFormSubmit}
                        renderCardBrands={renderCardBrands}
                    />
                </Elements>
            </StripeProvider>
        </div>
    );
};

PowerCheckoutForm.propTypes = {
    interval: PropTypes.string.isRequired,
    planData: PropTypes.shape({
        planId: PropTypes.string,
        interval: PropTypes.string,
        amountPayPerRecurringInterval: PropTypes.number,
        displayAmountPayPerRecurringInterval: PropTypes.string,
        displayAnnualAmount: PropTypes.string,
        displayMonthlyAmount: PropTypes.string,
        savingsPercentage: PropTypes.number,
    }),
    onComplete: PropTypes.func.isRequired,
    onPaymentAttempted: PropTypes.func,
    onError: PropTypes.func,
    hideHeader: PropTypes.bool,
    themeV3: PropTypes.bool,
    hideCardError: PropTypes.bool,
    couponCode: PropTypes.string,
    couponError: PropTypes.string,
    applyCoupon: PropTypes.func,
    invoiceItems: PropTypes.shape({
        lineItems: PropTypes.arrayOf(PropTypes.shape({})),
        total: PropTypes.shape({}),
    }),
    onBinCountryMismatch: PropTypes.func,
    isLoadingPricing: PropTypes.bool,
    renderBeforeLineItem: PropTypes.func,
    isTennisAustraliaMember: PropTypes.bool,
    savingsPercentage: PropTypes.number,
    submitButtonText: PropTypes.string,
    referringSurface: PropTypes.string,
    gatedFeature: PropTypes.string,
    disabled: PropTypes.bool,
    onSubmitStart: PropTypes.func,
    onSubmitAborted: PropTypes.func,
    onCardError: PropTypes.func,
    renderCardBrands: PropTypes.func,
    sourceEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PowerCheckoutForm.defaultProps = {
    planData: null,
    onPaymentAttempted: null,
    onError: null,
    hideHeader: false,
    themeV3: false,
    hideCardError: false,
    couponCode: null,
    couponError: '',
    applyCoupon: null,
    invoiceItems: null,
    onBinCountryMismatch: null,
    isLoadingPricing: false,
    renderBeforeLineItem: null,
    isTennisAustraliaMember: false,
    savingsPercentage: 0,
    submitButtonText: null,
    referringSurface: null,
    gatedFeature: null,
    disabled: false,
    onSubmitStart: null,
    onSubmitAborted: null,
    onCardError: null,
    renderCardBrands: null,
    sourceEventId: null,
};

export default PowerCheckoutForm;
